import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth, useTheme, API } from '../App';
import { toast } from 'sonner';
import { 
  CircleDot, Sun, Moon, ArrowLeft, Download, FileImage, FileText,
  Share2, Bookmark, BookmarkCheck, Copy, Check, Code, Cpu
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const ArtViewer = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [artwork, setArtwork] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const isVectorMode = artwork?.generation_mode === 'vector' || artwork?.svg_data;

  useEffect(() => {
    fetchArtwork();
  }, [id]);

  const fetchArtwork = async () => {
    try {
      const res = await fetch(`${API}/artworks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setArtwork(data);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Failed to fetch artwork');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPNG = () => {
    if (!artwork) return;
    
    try {
      // Convert base64 to blob for better download handling
      const byteCharacters = atob(artwork.main_image_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandala-${artwork.intention.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PNG downloaded');
    } catch (err) {
      console.error('PNG download error:', err);
      toast.error('Failed to download PNG');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await fetch(`${API}/artworks/${id}/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const data = await res.json();
      
      // Convert base64 to blob for better download handling
      const byteCharacters = atob(data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandala-${artwork.intention.replace(/\s+/g, '-').toLowerCase()}-poster.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PDF poster downloaded');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to export PDF');
    }
  };

  const handleDownloadSVG = async () => {
    if (!artwork) return;
    
    try {
      const res = await fetch(`${API}/artworks/${id}/export/svg`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'SVG export failed');
      }
      
      const data = await res.json();
      
      // Create download from SVG string
      const blob = new Blob([data.svg_string], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandala-${artwork.intention.replace(/\s+/g, '-').toLowerCase()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('SVG downloaded - ready for laser cutting!');
    } catch (err) {
      console.error('SVG download error:', err);
      toast.error(err.message || 'Failed to export SVG');
    }
  };

  const handleSavePreset = async () => {
    if (!presetName.trim() || !artwork) return;
    setIsSaving(true);

    try {
      const res = await fetch(`${API}/presets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          name: presetName,
          intention: artwork.intention,
          tradition: artwork.tradition,
          geometry_framework: artwork.geometry_framework,
          symmetry_type: artwork.symmetry_type,
          color_palette: artwork.color_palette,
        }),
      });

      if (res.ok) {
        toast.success('Preset saved!');
        setShowPresetDialog(false);
        setPresetName('');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!artwork) {
    return null;
  }

  const { meaning_card } = artwork;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Gallery</span>
          </Link>

          <div className="flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl capitalize">{artwork.intention}</span>
            {isVectorMode && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-mono">
                φ Vector
              </span>
            )}
          </div>

          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleTheme}
            data-testid="viewer-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Artwork Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-4 overflow-hidden"
            >
              <div className="aspect-square rounded-xl overflow-hidden bg-background flex items-center justify-center p-6">
                {isVectorMode && artwork.svg_data ? (
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    data-testid="artwork-svg"
                  >
                    <div 
                      className="artwork-svg-container"
                      dangerouslySetInnerHTML={{ __html: artwork.svg_data }}
                      style={{ 
                        width: '90%',
                        height: '90%',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center'
                      }}
                    />
                    <style>{`
                      .artwork-svg-container svg {
                        width: 100% !important;
                        height: 100% !important;
                        max-width: 100%;
                        max-height: 100%;
                      }
                    `}</style>
                  </div>
                ) : (
                  <img
                    src={`data:image/png;base64,${artwork.main_image_base64}`}
                    alt={artwork.intention}
                    className="w-[90%] h-[90%] object-contain"
                    data-testid="artwork-image"
                  />
                )}
              </div>

              {/* Export Buttons */}
              <div className="flex flex-wrap gap-3 mt-4">
                {isVectorMode && (
                  <Button onClick={handleDownloadSVG} className="rounded-full gap-2" data-testid="download-svg-btn">
                    <Code className="w-4 h-4" />
                    Download SVG
                  </Button>
                )}
                <Button onClick={handleDownloadPNG} variant={isVectorMode ? "outline" : "default"} className="rounded-full gap-2" data-testid="download-png-btn">
                  <FileImage className="w-4 h-4" />
                  Download PNG
                </Button>
                <Button onClick={handleDownloadPDF} variant="outline" className="rounded-full gap-2" data-testid="download-pdf-btn">
                  <FileText className="w-4 h-4" />
                  Download PDF Poster
                </Button>
                <Dialog open={showPresetDialog} onOpenChange={setShowPresetDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full gap-2" data-testid="save-preset-btn">
                      <Bookmark className="w-4 h-4" />
                      Save as Preset
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Save Energy Preset</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="preset-name">Preset Name</Label>
                        <Input
                          id="preset-name"
                          placeholder="e.g., Morning Meditation"
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          data-testid="preset-name-input"
                        />
                      </div>
                      <Button 
                        onClick={handleSavePreset} 
                        disabled={!presetName.trim() || isSaving}
                        className="w-full"
                        data-testid="confirm-save-preset-btn"
                      >
                        {isSaving ? 'Saving...' : 'Save Preset'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </motion.div>

            {/* Meaning Card & Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Tabs defaultValue="meaning" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="meaning" data-testid="tab-meaning">Meaning Card</TabsTrigger>
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                </TabsList>

                <TabsContent value="meaning">
                  <div className="glass-card p-8 space-y-6" data-testid="meaning-card">
                    <div className="text-center border-b border-border pb-6">
                      <h2 className="font-heading text-3xl mb-2">{meaning_card.title}</h2>
                      <p className="text-primary text-lg italic">"{meaning_card.intention}"</p>
                    </div>

                    <div>
                      <h3 className="font-heading text-xl mb-3">Sacred Symbolism</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {meaning_card.description}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-heading text-xl mb-3">Geometry Meaning</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {meaning_card.geometry_meaning}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-heading text-xl mb-3">Symmetry Significance</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {meaning_card.symmetry_meaning}
                      </p>
                    </div>

                    <div className="bg-primary/5 rounded-xl p-6">
                      <h3 className="font-heading text-xl mb-3">Ritual Prompt</h3>
                      <p className="text-foreground leading-relaxed italic">
                        {meaning_card.ritual_prompt}
                      </p>
                    </div>

                    <div className="bg-accent/5 rounded-xl p-6">
                      <h3 className="font-heading text-xl mb-3">Affirmation</h3>
                      <p className="text-foreground leading-relaxed">
                        {meaning_card.affirmation}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() => copyToClipboard(meaning_card.affirmation)}
                        data-testid="copy-affirmation-btn"
                      >
                        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="details">
                  <div className="glass-card p-8" data-testid="details-card">
                    <h2 className="font-heading text-2xl mb-6">Creation Details</h2>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-border">
                        <span className="text-muted-foreground">Intention</span>
                        <span className="font-medium capitalize">{artwork.intention}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-border">
                        <span className="text-muted-foreground">Tradition</span>
                        <span className="font-medium capitalize">{artwork.tradition.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-border">
                        <span className="text-muted-foreground">Geometry Framework</span>
                        <span className="font-medium capitalize">{artwork.geometry_framework.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-border">
                        <span className="text-muted-foreground">Symmetry</span>
                        <span className="font-medium">{artwork.symmetry_type}-fold</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-border">
                        <span className="text-muted-foreground">Color Palette</span>
                        <span className="font-medium capitalize">{artwork.color_palette}</span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="text-muted-foreground">Created</span>
                        <span className="font-medium">
                          {new Date(artwork.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-border">
                      <h3 className="font-heading text-lg mb-4">Quick Actions</h3>
                      <div className="flex flex-wrap gap-3">
                        <Link to="/studio">
                          <Button variant="outline" className="rounded-full" data-testid="create-similar-btn">
                            Create Similar
                          </Button>
                        </Link>
                        <Button 
                          variant="outline" 
                          className="rounded-full"
                          onClick={() => copyToClipboard(`${window.location.origin}/artwork/${artwork.artwork_id}`)}
                          data-testid="share-link-btn"
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share Link
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ArtViewer;
